import React from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import { Form } from 'react-final-form';

import Input from 'components/form/Input';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
  },
  title: {
    fontWeight: 700,
    fontSize: 22,
    padding: theme.spacing(3, 4, 2),
  },
  close: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  content: {
    padding: theme.spacing(0, 4, 3),
  },
  body: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '24px',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  imageWrapper: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  imageFallback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  actionTitle: {
    fontWeight: 700,
    fontSize: 18,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  hint: {
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  actions: {
    padding: theme.spacing(3, 4, 4),
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },
}));

type DeclineDialogProps = {
  open: boolean;
  title: string;
  name?: string;
  email?: string;
  screenshotUrl?: string;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (values: { note: string }) => Promise<void> | void;
};

const DeclineDialog = ({
  open,
  title,
  name,
  email,
  screenshotUrl,
  submitting = false,
  onClose,
  onSubmit,
}: DeclineDialogProps) => {
  const classes = useStyles();

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{ note: '' }}
      render={({ handleSubmit }) => (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          classes={{ paper: classes.dialogPaper }}>
          <form onSubmit={handleSubmit}>
            <IconButton className={classes.close} onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
            <DialogTitle className={classes.title}>Decline reason</DialogTitle>
            <DialogContent className={classes.content}>
              <Box className={classes.body}>
                <Box className={classes.imageWrapper}>
                  {screenshotUrl ? (
                    <img src={screenshotUrl} alt={title} className={classes.image} />
                  ) : (
                    <Box className={classes.imageFallback}>No screenshot</Box>
                  )}
                </Box>
                <Box className={classes.details}>
                  <Typography className={classes.actionTitle}>{title}</Typography>
                  {name ? <Typography>{name}</Typography> : null}
                  {email ? <Typography className={classes.subtitle}>{email}</Typography> : null}
                  <Typography className={classes.hint}>
                    Let the user know why their submission was declined. This note is optional.
                  </Typography>
                  <Input
                    name="note"
                    label="Decline reason"
                    inputProps={{
                      multiline: true,
                      rows: 4,
                      placeholder: 'Explain the reason for the decline',
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Button
                onClick={onClose}
                disabled={submitting}
                fullWidth
                variant="outlined"
                size="large">
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                disabled={submitting}
                fullWidth
                size="large">
                {submitting ? 'Sending...' : 'Send'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    />
  );
};

export default DeclineDialog;
